import { TerminalIcon } from "@heroicons/react/outline";

import ThinkingAccordion from "../ThinkingAccordion";
import { CopyButton } from "../ui/copy-button";
import { Citations } from "../Citations";

import { formatJson, getThinkingContent } from "@/utils/utils";
import { Message } from "@/types";

const TRUNCATE_PREVIEW_LENGTH = 100;

const ActionCallPreview = ({ message }: { message: Message }) => {
  if (message.role !== "assistant") {
    return null;
  }

  if (message.tool_calls) {
    return (
      <div className="group-hover:inherit pt-2 text-left font-mono">
        {message.tool_calls?.map((tool_call, index: number) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="flex items-center">
            <TerminalIcon className="mr-1 h-4 w-4 text-gray-500" />
            <span>
              {`${tool_call.function.name} <- ${tool_call.function.arguments}`.substring(
                0,
                TRUNCATE_PREVIEW_LENGTH,
              )}
              {tool_call.function.name.length + tool_call.function.arguments.length >
              TRUNCATE_PREVIEW_LENGTH
                ? "..."
                : ""}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="group-hover:inherit pt-2 text-left font-mono">
      <TerminalIcon className="mr-1 inline h-4 w-4 text-gray-500" />
      {message.function_call
        ? `${message.function_call.name} <- ${message.function_call.arguments}`.substring(
            0,
            TRUNCATE_PREVIEW_LENGTH,
          ) +
          (message.function_call.name.length + message.function_call.arguments.length >
          TRUNCATE_PREVIEW_LENGTH
            ? "..."
            : "")
        : null}
    </div>
  );
};

const ActionCallExpanded = ({
  message,
  thinkingContent,
}: {
  message: Message;
  thinkingContent: string | null;
}) => {
  if (message.role !== "assistant") {
    return null;
  }

  if (message.tool_calls) {
    return (
      <>
        {thinkingContent && (
          <ThinkingAccordion
            key="thinking-content-tool-call"
            value={thinkingContent}
            id="thinking-content-tool-call"
            title="Thinking"
            className="mb-4"
          />
        )}
        {message.tool_calls.map(tool_call => (
          <div key={tool_call.id || tool_call.function.name} className="font-mono">
            <div className="flex items-center font-medium text-gray-900">
              <TerminalIcon className="mr-2 inline h-4 w-4" />
              <span>{tool_call.function.name}</span>
            </div>
            <div className="pl-2 pt-1 text-gray-600">
              <div className="font-mono">
                <div className="font-medium text-gray-900">
                  <div className="flex items-start justify-between">
                    <div className="w-full overflow-x-auto">
                      {formatJson(tool_call.function.arguments)}
                    </div>
                    <div>
                      <CopyButton text={tool_call.function.arguments} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        <Citations messages={[message]} />
      </>
    );
  }

  return (
    <>
      {thinkingContent && (
        <ThinkingAccordion
          key="thinking-content-function-call"
          value={thinkingContent}
          id="thinking-content-function-call"
          title="Thinking"
          className="mb-4"
        />
      )}
      <div className="font-mono">
        <div className="flex items-center font-medium text-gray-900">
          <TerminalIcon className="mr-2 inline h-4 w-4" />
          <span>{message.function_call?.name}</span>
        </div>
        <div className="pl-2 pt-1 text-gray-600">
          <div className="font-mono">
            <div className="font-medium text-gray-900">
              <div className="flex items-start justify-between">
                <div className="w-full">
                  <div className="w-full overflow-x-auto">
                    {formatJson(message.function_call?.arguments || "")}
                  </div>
                </div>
                <div>
                  <CopyButton text={message.function_call?.arguments ?? ""} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Citations messages={[message]} />
    </>
  );
};

export const ActionCall = ({
  componentIsOpen,
  message,
}: {
  componentIsOpen: boolean;
  message: Message;
}) => {
  if (message.role !== "assistant") {
    return null;
  }
  const thinkingContent = getThinkingContent(message);

  if (!componentIsOpen) {
    return <ActionCallPreview message={message} />;
  }

  return <ActionCallExpanded message={message} thinkingContent={thinkingContent} />;
};
